import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import HomeDashboardLayout from '../homeDashboard/HomeDashboardLayout';
import { DASHBOARD_ROUTES } from '../homeDashboard/dashboardRoutes';
import { useSetUserExamCategory } from '../hooks/useSetUserExamCategory';
import statesIllustration from '../assets/images/onboarding-states-illustration.png';
import categoriesIllustration from '../assets/images/onboarding-exam-categories-illustration.png';
import favoriteExamsIllustration from '../assets/images/onboarding-favorite-exams-illustration.png';
import { useStates } from '../hooks/useStates';
import { useExamsByCategory } from '../hooks/useExamsByCategory';
import { useAddStateFavourite } from '../hooks/useAddStateFavourite';
import { saveResidentialState } from '../services/statesService';
import { fetchMasterExamCategories, updateExamFavourite } from '../services/examCategoryService';
import { SETTINGS_ROUTES } from '../settingProfile/settingsRoutes';
import { ONBOARDING_ROUTES } from './onboardingRoutes';
import '../homeDashboard/DashboardSkeleton.css';
import './SignupSelections.css';

const stateIdFrom = (states) => Number(states?.find((state) => Number(state.is_user_state) === 1)?.state_id || 0);
const errorText = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

function useSignupCategories(stateId) {
  return useQuery({
    queryKey: ['signupExamCategories', stateId],
    queryFn: async () => {
      const data = await fetchMasterExamCategories({ stateId });
      return Array.isArray(data?.categories_list) ? data.categories_list : [];
    },
    enabled: Boolean(stateId),
    retry: 1,
  });
}

function SelectionCardsShimmer({ exams = false }) {
  return (
    <div className="signup-selections__grid signup-selections__shimmer-grid" role="status" aria-label={exams ? 'Loading exams' : 'Loading exam categories'}>
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className="signup-selections__card signup-selections__shimmer-card" aria-hidden="true">
          <div className="signup-selections__shimmer-image dashboard-skeleton" />
          <div className="signup-selections__shimmer-content">
            <div className="signup-selections__shimmer-title dashboard-skeleton" />
            <div className="signup-selections__shimmer-subtitle dashboard-skeleton" />
          </div>
          <div className={`signup-selections__shimmer-action dashboard-skeleton${exams ? ' signup-selections__shimmer-action--checkbox' : ''}`} />
        </div>
      ))}
    </div>
  );
}

function SelectionPage({ dashboard = false, step, title, description, children, illustration, back, backDisabled = false, next, nextDisabled, nextLabel = 'Continue' }) {
  const navigate = useNavigate();
  if (dashboard) return (
    <HomeDashboardLayout showHeader={false}>
      <div role="main" className="page-padding dashboard-exam-selection">
        <section className="signup-selections">
          <div className="signup-selections__top">
            <button type="button" className="signup-selections__back" disabled={backDisabled}
              aria-label="Go back" onClick={() => typeof back === 'function' ? back() : navigate(back || DASHBOARD_ROUTES.home)}>
              <i className="bi bi-chevron-left" aria-hidden="true" />
            </button>
          </div>
          <div className="rapid-fire-cardd">
              <h1>{title}</h1>
              <p className="signup-selections__intro">{description}</p>
          </div>
          <div className="favState">{children}</div>
             {next && <div className="signup-selections__footer">
             <button type="button" className="signup-selections__continue" onClick={next} disabled={nextDisabled}>{nextLabel}</button>
          </div>}
        </section>
      </div>
    </HomeDashboardLayout>
  );
  return (

    <div className="container-fluid signin-container auth-background mobile-spacing">
    <div className="row auth-page-row">
      {/* Left Section */}
      <div className="col-md-6 d-flex flex-column justify-content-center align-items-center text-white left-section p-0 signup-selections-illustration-panel">
        {illustration && <img className="signup-selections-illustration" src={illustration} alt="Student learning on a laptop" />}
      </div>

      {/* Right Section */}
      <div className="col-md-6 d-flex justify-content-center align-items-center right-section">
        <div className="signup-selections-page">
          {/* <header className="signup-selections-page__header">
            <img src={brandLogo} alt="Vidyakasam Education" />
          </header> */}

          <div className="signup-selections" role="main">
            <div className="signup-selections__top">
              {back && (
                <button
                  type="button"
                  className="signup-selections__back"
                  onClick={() =>
                    typeof back === "function"
                      ? back()
                      : navigate(back)
                  }
                  disabled={backDisabled}
                  aria-label="Go back"
                >
                  <i className="bi bi-arrow-left" />
                </button>
              )}

              <span className="signup-selections__step">
                Step {step} of 3
              </span>
            </div>

            <h1>{title}</h1>
          <p className="signup-selections__intro">
              {description}
            </p>
<div className="favState">
            {children}
   </div>
            {next && (
              <div className="signup-selections__footer">
                <button
                  type="button"
                  className="signup-selections__continue"
                  onClick={next}
                  disabled={nextDisabled}
                >
                  {nextLabel}
                </button>
              </div>
            )}
          
          </div>
        </div>
      </div>
    </div>
  </div>
);
}


export function SignupStates() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, isPlaceholderData, isError, error, refetch } = useStates();
  const loadingStates = isLoading || isPlaceholderData;
  const states = data?.states || [];
  const persistedStateId = stateIdFrom(states);
  const [selection, setSelection] = useState(null);
  const [saveError, setSaveError] = useState('');
  const selectedStateId = selection?.id ?? persistedStateId;
  const residentialState = useMutation({ mutationFn: saveResidentialState });
  const favoriteState = useAddStateFavourite();

  const chooseState = async (state) => {
    const id = Number(state?.state_id);
    if (!id || residentialState.isPending || favoriteState.isPending || Number(state.is_disabled) === 1) return;
    if (id === selectedStateId && (selection?.saved || id === persistedStateId)) return;
    setSaveError('');
    setSelection({ id, saved: false });
    try {
      await residentialState.mutateAsync(id);
      setSelection({ id, saved: true });
      queryClient.setQueryData(['states'], (current) => current ? {
        ...current,
        states: current.states.map((item) => ({ ...item, is_user_state: Number(item.state_id) === id ? 1 : 0 })),
      } : current);
      queryClient.invalidateQueries({ queryKey: ['states'] });
    } catch (saveFailure) {
      setSelection(null);
      setSaveError(errorText(saveFailure, 'Unable to save residential state.'));
    }
  };

  const toggleFavorite = async (state) => {
    if (favoriteState.isPending || residentialState.isPending) return;
    setSaveError('');
    try {
      await favoriteState.mutateAsync({ state_id: Number(state.state_id), status: Number(state.is_favourite) === 1 ? 0 : 1 });
    } catch (saveFailure) {
      setSaveError(errorText(saveFailure, 'Unable to update favorite state.'));
    }
  };

  const canContinue = Boolean(selectedStateId && (selection?.saved || selectedStateId === persistedStateId)) && !residentialState.isPending && !favoriteState.isPending && !loadingStates && !isError;

  return <SelectionPage step={1} title="Choose your states" description="Select your residential state, then choose any states you want to follow." illustration={statesIllustration} next={() => navigate(ONBOARDING_ROUTES.categories)} nextDisabled={!canContinue}>
    <section className="signup-selections__section">
      <h2>Residential state</h2>
      <select aria-label="Residential state" value={selectedStateId || ''} onChange={(event) => chooseState(states.find((state) => Number(state.state_id) === Number(event.target.value)))} disabled={loadingStates || residentialState.isPending || favoriteState.isPending}>
        <option value="">Select your state</option>
        {states.filter((state) => Number(state.is_disabled) !== 1).map((state) => <option key={state.state_id} value={state.state_id}>{state.state_name}</option>)}
      </select>
      {residentialState.isPending && <p className="signup-selections__muted">Saving residential state...</p>}
    </section>
    <section className="signup-selections__section favState">
      <h2>Favorite states</h2>
      <p className="signup-selections__muted">Choose as many as you like. You can continue without selecting any.</p>
      {loadingStates && <p role="status">Loading states...</p>}
      {isError && <p role="alert">{errorText(error, 'Unable to load states.')} <button type="button" onClick={() => refetch()}>Retry</button></p>}
      {!loadingStates && !isError && !states.length && <p>No states available.</p>}
      <div className="signup-selections__grid">
        {states.map((state) => <label key={state.state_id} className={`signup-selections__card signup-selections__card--choice${Number(state.is_favourite) === 1 ? ' is-selected' : ''}${Number(state.is_disabled) === 1 ? ' is-disabled' : ''}`}>
          {state.state_image && <img src={state.state_image} alt="" />}
          <span><strong>{state.state_name}</strong>{state.capital_name && <small>{state.capital_name}</small>}</span>
          <input type="checkbox" checked={Number(state.is_favourite) === 1} disabled={favoriteState.isPending || residentialState.isPending || Number(state.is_disabled) === 1} onChange={() => toggleFavorite(state)} />
        </label>)}
      </div>
    </section>
    {saveError && <p role="alert" className="signup-selections__error">{saveError}</p>}
  </SelectionPage>;
}

export function SignupExamCategories({ dashboard = false }) {
  const navigate = useNavigate();
  const { data: statesData, isLoading: statesLoading, isPlaceholderData: statesPlaceholder, isError: statesError } = useStates();
  const loadingStates = statesLoading || statesPlaceholder;
  const stateId = stateIdFrom(statesData?.states);
  const { data: categories = [], isLoading, isError, error, refetch } = useSignupCategories(stateId);
  const showCategoryShimmer = !statesError && !isError && (loadingStates || (stateId > 0 && isLoading));
  const categoryMutation = useSetUserExamCategory();
  const [saveError, setSaveError] = useState('');

  const selectCategory = async (category) => {
    if (categoryMutation.isPending || loadingStates || statesError || isError || !stateId) return;
    setSaveError('');
    try {
      await categoryMutation.mutateAsync({ exam_category_id: Number(category.category_id) });
      navigate(dashboard
        ? DASHBOARD_ROUTES.selectExams
        : ONBOARDING_ROUTES.exams, { state: { categoryId: Number(category.category_id) } });
    } catch (saveFailure) {
      setSaveError(errorText(saveFailure, 'Unable to select exam category.'));
    }
  };

  return <SelectionPage dashboard={dashboard} step={2} title="Choose an exam category" description="Select the category that matches your preparation." illustration={categoriesIllustration} back={dashboard ? DASHBOARD_ROUTES.home : ONBOARDING_ROUTES.states} backDisabled={categoryMutation.isPending}>
    {statesError && <p role="alert">Unable to load your residential state. <button type="button" onClick={() => navigate(dashboard ? DASHBOARD_ROUTES.states : ONBOARDING_ROUTES.states)}>Choose state</button></p>}
    {!loadingStates && !statesError && !stateId && <p>Select a residential state first. <button type="button" onClick={() => navigate(dashboard ? DASHBOARD_ROUTES.states : ONBOARDING_ROUTES.states)}>Choose state</button></p>}
    {isError && <p role="alert">{errorText(error, 'Unable to load exam categories.')} <button type="button" onClick={() => refetch()}>Retry</button></p>}
    {!showCategoryShimmer && !isLoading && !isError && stateId > 0 && !categories.length && <p>No exam categories are available for this state.</p>}
    {showCategoryShimmer ? <SelectionCardsShimmer /> : <div className="signup-selections__grid">
      {categories.map((category) => <button type="button" key={category.category_id} className="signup-selections__card signup-selections__card--choice signup-selections__category" disabled={categoryMutation.isPending || loadingStates || statesError || isError} onClick={() => selectCategory(category)}>
        {(category.icon_url || category.image_url) && <img src={category.icon_url || category.image_url} alt="" />}
        <span><strong>{category.name}</strong><small>{Number(category.exam_count || 0)} exams · {Number(category.favorite_count || 0)} favorites</small></span>
        <i className="bi bi-chevron-right" aria-hidden="true" />
      </button>)}
    </div>}
    {saveError && <p role="alert" className="signup-selections__error">{saveError}</p>}
  </SelectionPage>;
}

export function SignupFavoriteExams({ dashboard = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: statesData, isLoading: statesLoading, isPlaceholderData: statesPlaceholder, isError: statesError, refetch: refetchStates } = useStates();
  const loadingStates = statesLoading || statesPlaceholder;
  const stateId = stateIdFrom(statesData?.states);
  const { data: categories = [], isLoading: categoriesLoading, isError: categoriesError, refetch: refetchCategories } = useSignupCategories(stateId);
  const routeCategoryId = Number(location.state?.categoryId || (dashboard ? new URLSearchParams(location.search).get('category_id') : 0) || 0);
  const categoryId = routeCategoryId > 0 && (loadingStates || categoriesLoading || categories.some((category) => Number(category.category_id) === routeCategoryId))
    ? routeCategoryId
    : Number(categories.find((category) => Number(category.default_category) === 1)?.category_id || 0);
  const { data, isLoading, isPlaceholderData, isError, error, refetch } = useExamsByCategory(categoryId, { isHome: false });
  const loadingExams = isLoading || isPlaceholderData;
  const showExamsShimmer = !statesError && !categoriesError && !isError && (loadingStates || categoriesLoading || (categoryId > 0 && loadingExams));
  const exams = data?.examsList || [];
  const [selected, setSelected] = useState({});
  const [saveError, setSaveError] = useState('');
  const favoriteMutation = useMutation({
    mutationFn: updateExamFavourite,
    onSuccess: async (_response, { exam_id: id, status }) => {
      queryClient.setQueryData(['examsByCategory', categoryId, 0], (current) => current ? {
        ...current,
        examsList: current.examsList.map((item) => Number(item.md_exams_id) === Number(id) ? { ...item, is_favourite: status } : item),
      } : current);
      await Promise.all(['examsByCategory', 'signupExamCategories', 'favoriteCategories', 'examCategories', 'dashboardHeaderData', 'dashboardContentData', 'dashboardStatsData', 'sidebarData'].map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      ));
    },
  });

  useEffect(() => {
    if (favoriteMutation.isPending) return;
    setSelected(Object.fromEntries((data?.examsList || []).map((exam) => [exam.md_exams_id, Number(exam.is_favourite) === 1])));
  }, [data?.examsList, favoriteMutation.isPending]);

  const toggleExam = async (exam) => {
    if (favoriteMutation.isPending) return;
    const id = Number(exam.md_exams_id);
    const previous = Boolean(selected[id]);
    setSaveError('');
    setSelected((current) => ({ ...current, [id]: !previous }));
    try {
      await favoriteMutation.mutateAsync({ exam_id: id, status: previous ? 0 : 1 });

    } catch (saveFailure) {
      setSelected((current) => ({ ...current, [id]: previous }));
      setSaveError(errorText(saveFailure, 'Unable to update favorite exam.'));
    }
  };

  const backToCategories = () => {
    queryClient.invalidateQueries({ queryKey: ['signupExamCategories', stateId] });
    navigate(dashboard ? DASHBOARD_ROUTES.examCategories : ONBOARDING_ROUTES.categories);
  };
  const selectedCount = exams.filter((exam) => selected[exam.md_exams_id]).length;

  return <SelectionPage dashboard={dashboard} step={3} title="Choose your favorite exams" description={`Select at least one exam${data?.categoryName ? ` in ${data.categoryName}` : ''}.`} illustration={favoriteExamsIllustration} back={backToCategories} backDisabled={favoriteMutation.isPending} nextLabel={dashboard ? "Done" : "Continue"} next={() => navigate(dashboard ? DASHBOARD_ROUTES.home : SETTINGS_ROUTES.profile)} nextDisabled={!categoryId || loadingStates || categoriesLoading || loadingExams || isError || categoriesError || statesError || favoriteMutation.isPending || selectedCount < 1}>
    {statesError && <p role="alert">Unable to load your state. <button type="button" onClick={() => refetchStates()}>Retry</button></p>}
    {categoriesError && <p role="alert">Unable to load categories. <button type="button" onClick={() => refetchCategories()}>Retry</button></p>}
    {!loadingStates && !categoriesLoading && !categoriesError && !statesError && !categoryId && <p>Choose an exam category first. <button type="button" onClick={backToCategories}>Choose category</button></p>}
    {isError && <p role="alert">{errorText(error, 'Unable to load exams.')} <button type="button" onClick={() => refetch()}>Retry</button></p>}
    {!showExamsShimmer && !loadingExams && !isError && categoryId > 0 && !exams.length && <p>No exams are available in this category.</p>}
    {showExamsShimmer ? <SelectionCardsShimmer exams /> : <div className="signup-selections__grid">
      {exams.map((exam) => <label key={exam.md_exams_id} className={`signup-selections__card signup-selections__card--choice${selected[exam.md_exams_id] ? ' is-selected' : ''}`}>
        {exam.exam_image && <img src={exam.exam_image} alt="" />}
        <span><strong>{exam.exam_name}</strong>{exam.state_name && <small>{exam.state_name}</small>}</span>
        <input type="checkbox" checked={Boolean(selected[exam.md_exams_id])} disabled={favoriteMutation.isPending || loadingExams || isError || categoriesError || statesError} onChange={() => toggleExam(exam)} />
      </label>)}
    </div>}
    {saveError && <p role="alert" className="signup-selections__error">{saveError}</p>}
  </SelectionPage>;
}
